import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  Button,
  Space,
  Typography,
  Tag,
  Alert,
  Collapse,
  Progress,
  Row,
  Col,
  Statistic,
  Empty,
  Spin,
  List,
  Divider,
} from "antd";
import {
  SafetyCertificateOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  BulbOutlined,
  ThunderboltOutlined,
  WarningOutlined,
  AuditOutlined,
} from "@ant-design/icons";
import { runValidation } from "../../../../Slices/policyCheckSlice";

const { Text, Title, Paragraph } = Typography;

const SEVERITY_CONFIG = {
  critical: { color: "#ff4d4f", icon: <CloseCircleOutlined />, tag: "red", label: "Critical" },
  warning: { color: "#faad14", icon: <ExclamationCircleOutlined />, tag: "orange", label: "Warning" },
  info: { color: "#1890ff", icon: <InfoCircleOutlined />, tag: "blue", label: "Info" },
};

const CHECK_STATUS_CONFIG = {
  pass: { color: "#52c41a", icon: <CheckCircleOutlined style={{ color: "#52c41a" }} />, label: "Pass" },
  fail: { color: "#ff4d4f", icon: <CloseCircleOutlined style={{ color: "#ff4d4f" }} />, label: "Fail" },
  warning: { color: "#faad14", icon: <ExclamationCircleOutlined style={{ color: "#faad14" }} />, label: "Warning" },
};

const INSIGHT_TYPE_CONFIG = {
  coverage_gap: { icon: <WarningOutlined />, color: "red", label: "Coverage Gap" },
  inconsistency: { icon: <ExclamationCircleOutlined />, color: "orange", label: "Inconsistency" },
  risk_flag: { icon: <ThunderboltOutlined />, color: "volcano", label: "Risk Flag" },
  recommendation: { icon: <BulbOutlined />, color: "green", label: "Recommendation" },
  unusual_item: { icon: <AuditOutlined />, color: "purple", label: "Unusual Item" },
};

const PolicyValidationView = ({ requestId, RequestInfo }) => {
  const dispatch = useDispatch();
  const { activeChecklist, validationResult, validateStatus } = useSelector(
    (state) => state.policyCheck
  );
  const [includeAI, setIncludeAI] = useState(true);

  const handleRunValidation = async () => {
    if (!activeChecklist) {
      return;
    }
    try {
      await dispatch(
        runValidation({
          checklistId: activeChecklist._id,
          includeAIAnalysis: includeAI,
        })
      ).unwrap();
    } catch (err) {
      // Error handled by Redux
    }
  };

  if (!activeChecklist) {
    return (
      <Card>
        <Empty description="Create a checklist first to run validation and AI analysis" />
      </Card>
    );
  }

  const isRunning = validateStatus === "validating";

  return (
    <div>
      {/* Run Validation Controls */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Button
                type="primary"
                icon={<SafetyCertificateOutlined />}
                onClick={handleRunValidation}
                loading={isRunning}
              >
                {validationResult ? "Re-run" : "Run"} Validation & AI Analysis
              </Button>
              <Button
                size="small"
                type={includeAI ? "primary" : "default"}
                ghost={includeAI}
                onClick={() => setIncludeAI(!includeAI)}
              >
                {includeAI ? "AI Analysis: ON" : "AI Analysis: OFF"}
              </Button>
            </Space>
          </Col>
          {validationResult && (
            <Col>
              <Space size="large">
                <Statistic
                  title="Score"
                  value={validationResult.score}
                  suffix="/ 100"
                  valueStyle={{
                    color: validationResult.score >= 80 ? "#52c41a" : validationResult.score >= 50 ? "#faad14" : "#ff4d4f",
                    fontSize: 24,
                  }}
                />
                <Statistic
                  title="Critical"
                  value={validationResult.criticalCount}
                  valueStyle={{ color: validationResult.criticalCount > 0 ? "#ff4d4f" : "#52c41a" }}
                />
                <Statistic
                  title="Warnings"
                  value={validationResult.warningCount}
                  valueStyle={{ color: validationResult.warningCount > 0 ? "#faad14" : "#52c41a" }}
                />
                <Statistic
                  title="Passed"
                  value={validationResult.passedChecks}
                  suffix={`/ ${validationResult.totalChecks}`}
                  valueStyle={{ color: "#52c41a" }}
                />
              </Space>
            </Col>
          )}
        </Row>
      </Card>

      {isRunning && (
        <Card>
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin size="large" tip="Running validation checks and AI analysis..." />
          </div>
        </Card>
      )}

      {validationResult && !isRunning && (
        <>
          {/* Score Progress */}
          <Card size="small" style={{ marginBottom: 16, textAlign: "center" }}>
            <Progress
              type="dashboard"
              percent={validationResult.score}
              strokeColor={{
                "0%": "#ff4d4f",
                "50%": "#faad14",
                "100%": "#52c41a",
              }}
              format={(pct) => (
                <div>
                  <div style={{ fontSize: 28, fontWeight: "bold" }}>{pct}%</div>
                  <div style={{ fontSize: 12, color: "#888" }}>Validation Score</div>
                </div>
              )}
              width={150}
            />
          </Card>

          {/* Validation Checks grouped by category */}
          <Card
            title={
              <Space>
                <CheckCircleOutlined /> Validation Checks
                <Tag>{validationResult.passedChecks}/{validationResult.totalChecks} passed</Tag>
              </Space>
            }
            size="small"
            style={{ marginBottom: 16 }}
          >
            {renderChecksByCategory(validationResult.checks)}
          </Card>

          {/* AI Insights */}
          {validationResult.aiInsights && validationResult.aiInsights.length > 0 && (
            <Card
              title={
                <Space>
                  <BulbOutlined /> AI Insights & Recommendations
                  <Tag color="purple">{validationResult.aiInsights.length} findings</Tag>
                </Space>
              }
              size="small"
            >
              <List
                dataSource={validationResult.aiInsights}
                renderItem={(insight) => {
                  const typeConf = INSIGHT_TYPE_CONFIG[insight.type] || INSIGHT_TYPE_CONFIG.recommendation;
                  const sevConf = SEVERITY_CONFIG[insight.severity] || SEVERITY_CONFIG.info;

                  return (
                    <List.Item>
                      <List.Item.Meta
                        avatar={
                          <Space direction="vertical" align="center" size={4}>
                            <Tag icon={typeConf.icon} color={typeConf.color}>
                              {typeConf.label}
                            </Tag>
                            <Tag color={sevConf.tag}>{sevConf.label}</Tag>
                          </Space>
                        }
                        title={<Text strong>{insight.title}</Text>}
                        description={
                          <div>
                            <Paragraph style={{ marginBottom: 4 }}>{insight.message}</Paragraph>
                            {insight.relatedFields && insight.relatedFields.length > 0 && (
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                Related: {insight.relatedFields.join(", ")}
                              </Text>
                            )}
                          </div>
                        }
                      />
                    </List.Item>
                  );
                }}
              />
            </Card>
          )}

          {validationResult.aiInsights && validationResult.aiInsights.length === 0 && (
            <Alert
              type="success"
              message="No AI concerns found"
              description="The AI analysis did not find any notable coverage gaps, inconsistencies, or risk flags in this policy."
              showIcon
            />
          )}
        </>
      )}
    </div>
  );
};

function renderChecksByCategory(checks) {
  const categories = {};
  for (const check of checks) {
    if (!categories[check.category]) categories[check.category] = [];
    categories[check.category].push(check);
  }

  return (
    <Collapse
      defaultActiveKey={Object.keys(categories).filter((cat) =>
        categories[cat].some((c) => c.status === "fail")
      )}
      items={Object.entries(categories).map(([category, items]) => {
        const failCount = items.filter((i) => i.status === "fail").length;
        const passCount = items.filter((i) => i.status === "pass").length;

        return {
          key: category,
          label: (
            <Space>
              <Text strong>{category}</Text>
              <Tag color="green">{passCount} pass</Tag>
              {failCount > 0 && <Tag color="red">{failCount} fail</Tag>}
            </Space>
          ),
          children: (
            <List
              size="small"
              dataSource={items}
              renderItem={(item) => {
                const statusConf = CHECK_STATUS_CONFIG[item.status] || CHECK_STATUS_CONFIG.pass;
                const sevConf = SEVERITY_CONFIG[item.severity] || SEVERITY_CONFIG.info;

                return (
                  <List.Item>
                    <List.Item.Meta
                      avatar={statusConf.icon}
                      title={
                        <Space>
                          <Text>{item.checkName}</Text>
                          <Tag color={sevConf.tag} size="small">
                            {sevConf.label}
                          </Tag>
                        </Space>
                      }
                      description={item.message}
                    />
                  </List.Item>
                );
              }}
            />
          ),
        };
      })}
    />
  );
}

export default PolicyValidationView;
